import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import audioToolsService from '../../services/audioTools';

const PCM_TARGET_RATE = 16000;
const PCM_FLUSH_SECONDS = 3;

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function pcmToWavBase64(samples, sampleRate) {
  const numSamples = samples.length;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, numSamples * 2, true);
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function downsamplePCM(samples, fromRate, toRate) {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const newLength = Math.round(samples.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const low = Math.floor(srcIndex);
    const high = Math.min(low + 1, samples.length - 1);
    const frac = srcIndex - low;
    result[i] = samples[low] * (1 - frac) + samples[high] * frac;
  }
  return result;
}

function AudioToolsPanel({ activeSection = 'all' }) {
  const [speechFile, setSpeechFile] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechResult, setSpeechResult] = useState(null);

  const [realtimeTranscript, setRealtimeTranscript] = useState('');
  const [realtimePartial, setRealtimePartial] = useState('');
  const [realtimeError, setRealtimeError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [realtimeProcessing, setRealtimeProcessing] = useState(false);

  const [micDevices, setMicDevices] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState('');
  const [micTestLevel, setMicTestLevel] = useState(0);
  const [micTestStatus, setMicTestStatus] = useState('idle');
  const micTestStreamRef = useRef(null);
  const micTestContextRef = useRef(null);
  const micTestIntervalRef = useRef(null);

  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const pcmBufferRef = useRef([]);
  const pcmSampleCountRef = useRef(0);
  const realtimeDecodeErrorCountRef = useRef(0);
  const realtimeEmptyPartialCountRef = useRef(0);
  const realtimeFinalizeRequestedRef = useRef(false);
  const realtimeCloseTimerRef = useRef(null);

  const [voiceChangerFile, setVoiceChangerFile] = useState(null);
  const [voicePitch, setVoicePitch] = useState(0);
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [voiceGain, setVoiceGain] = useState(0);
  const [isVoiceChanging, setIsVoiceChanging] = useState(false);
  const [voiceChangedUrl, setVoiceChangedUrl] = useState('');
  const voiceChangedUrlRef = useRef('');

  const [sfxPrompt, setSfxPrompt] = useState('');
  const [sfxDuration, setSfxDuration] = useState(2);
  const [sfxIntensity, setSfxIntensity] = useState(0.7);
  const [isGeneratingSfx, setIsGeneratingSfx] = useState(false);
  const [sfxUrl, setSfxUrl] = useState('');
  const [sfxType, setSfxType] = useState('');
  const sfxUrlRef = useRef('');

  const [isolatorFile, setIsolatorFile] = useState(null);
  const [isolationStrength, setIsolationStrength] = useState(0.7);
  const [isIsolating, setIsIsolating] = useState(false);
  const [isolatorUrl, setIsolatorUrl] = useState('');
  const isolatorUrlRef = useRef('');

  const canUseRealtime =
    typeof window !== 'undefined' &&
    (typeof window.AudioContext !== 'undefined' || typeof window.webkitAudioContext !== 'undefined') &&
    Boolean(navigator?.mediaDevices?.getUserMedia);
  const shouldShowSection = (section) => activeSection === 'all' || activeSection === section;

  const refreshMicDevices = async () => {
    try {
      // Request permission first so labels are available
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach((t) => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === 'audioinput');
      setMicDevices(mics);
      if (!selectedMicId && mics.length > 0) {
        // Pre-select first non-default real mic if available
        const realMic = mics.find(
          (m) => m.deviceId !== 'default' && !m.label.toLowerCase().includes('stereo mix')
        );
        setSelectedMicId(realMic ? realMic.deviceId : mics[0].deviceId);
      }
    } catch {
      setMicDevices([]);
    }
  };

  const stopMicTest = () => {
    if (micTestIntervalRef.current) {
      clearInterval(micTestIntervalRef.current);
      micTestIntervalRef.current = null;
    }
    if (micTestContextRef.current) {
      try { micTestContextRef.current.close(); } catch {}
      micTestContextRef.current = null;
    }
    if (micTestStreamRef.current) {
      micTestStreamRef.current.getTracks().forEach((t) => t.stop());
      micTestStreamRef.current = null;
    }
    setMicTestLevel(0);
    setMicTestStatus('idle');
  };

  const startMicTest = async () => {
    stopMicTest();
    if (!selectedMicId) {
      toast.error('Select a microphone first');
      return;
    }
    setMicTestStatus('testing');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selectedMicId } },
      });
      micTestStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      micTestContextRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      micTestIntervalRef.current = setInterval(() => {
        analyser.getFloatTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) sumSq += buf[i] * buf[i];
        const rms = Math.sqrt(sumSq / buf.length);
        // Scale to 0-100 for display (0.1 RMS ≈ loud speech)
        setMicTestLevel(Math.min(100, Math.round(rms * 1000)));
      }, 100);
    } catch {
      toast.error('Cannot access selected microphone');
      setMicTestStatus('idle');
    }
  };

  const setBlobPreviewUrl = (blob, urlRef, setUrl) => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
    }
    const newUrl = URL.createObjectURL(blob);
    urlRef.current = newUrl;
    setUrl(newUrl);
  };

  const downloadBlobUrl = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const releaseMediaStream = () => {
    if (!mediaStreamRef.current) {
      return;
    }
    mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const clearRealtimeCloseTimer = () => {
    if (!realtimeCloseTimerRef.current) {
      return;
    }
    window.clearTimeout(realtimeCloseTimerRef.current);
    realtimeCloseTimerRef.current = null;
  };

  const closeRealtimeSocket = () => {
    if (!wsRef.current) {
      return;
    }
    if (
      wsRef.current.readyState === WebSocket.OPEN ||
      wsRef.current.readyState === WebSocket.CONNECTING
    ) {
      wsRef.current.close();
    }
    wsRef.current = null;
  };

  const stopAudioCapture = () => {
    if (scriptProcessorRef.current) {
      try { scriptProcessorRef.current.disconnect(); } catch {}
      scriptProcessorRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
    }
  };

  const flushPCMBuffer = () => {
    const buffer = pcmBufferRef.current;
    const count = pcmSampleCountRef.current;
    if (count === 0 || buffer.length === 0) return;
    const merged = new Float32Array(count);
    let offset = 0;
    for (const buf of buffer) {
      merged.set(buf, offset);
      offset += buf.length;
    }
    pcmBufferRef.current = [];
    pcmSampleCountRef.current = 0;
    const wavBase64 = pcmToWavBase64(merged, PCM_TARGET_RATE);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'chunk',
          audio_base64: wavBase64,
          mime_type: 'audio/wav',
          is_cumulative: false,
        })
      );
    }
  };

  const resetRealtimeResources = () => {
    realtimeFinalizeRequestedRef.current = false;
    clearRealtimeCloseTimer();
    stopAudioCapture();
    releaseMediaStream();
    closeRealtimeSocket();
    pcmBufferRef.current = [];
    pcmSampleCountRef.current = 0;
    realtimeDecodeErrorCountRef.current = 0;
    realtimeEmptyPartialCountRef.current = 0;
  };

  const finalizeRealtimeSession = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'finalize' }));
      clearRealtimeCloseTimer();
      realtimeCloseTimerRef.current = window.setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, 'completed');
        }
      }, 7000);
      return;
    }
    resetRealtimeResources();
    setRealtimeStatus('idle');
  };

  useEffect(() => {
    return () => {
      resetRealtimeResources();
      stopMicTest();
      if (voiceChangedUrlRef.current) {
        URL.revokeObjectURL(voiceChangedUrlRef.current);
      }
      if (sfxUrlRef.current) {
        URL.revokeObjectURL(sfxUrlRef.current);
      }
      if (isolatorUrlRef.current) {
        URL.revokeObjectURL(isolatorUrlRef.current);
      }
    };
  }, []);

  const handleSpeechToText = async (event) => {
    event.preventDefault();
    if (!speechFile) {
      toast.error('Select an audio file first');
      return;
    }

    setIsTranscribing(true);
    setSpeechResult(null);
    try {
      const result = await audioToolsService.speechToText(speechFile);
      setSpeechResult(result);
      toast.success('Transcription complete');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Speech-to-text failed';
      toast.error(message);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleStartRealtime = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in before starting realtime transcription');
      return;
    }
    if (!canUseRealtime) {
      const message = 'Realtime speech-to-text is not supported in this browser.';
      setRealtimeError(message);
      toast.error(message);
      return;
    }

    setRealtimeError(null);
    setRealtimePartial('');
    setRealtimeTranscript('');
    setRealtimeStatus('connecting');
    pcmBufferRef.current = [];
    pcmSampleCountRef.current = 0;
    realtimeDecodeErrorCountRef.current = 0;
    realtimeEmptyPartialCountRef.current = 0;
    realtimeFinalizeRequestedRef.current = false;
    clearRealtimeCloseTimer();
    stopMicTest();

    try {
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
      if (selectedMicId) {
        audioConstraints.deviceId = { exact: selectedMicId };
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      mediaStreamRef.current = stream;

      // Create AudioContext in user-gesture context so it starts in 'running' state
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioContextRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }

      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = processor;
      const contextRate = audioCtx.sampleRate;
      const samplesPerFlush = PCM_TARGET_RATE * PCM_FLUSH_SECONDS;

      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        const downsampled =
          contextRate !== PCM_TARGET_RATE
            ? downsamplePCM(inputData, contextRate, PCM_TARGET_RATE)
            : new Float32Array(inputData);
        pcmBufferRef.current.push(downsampled);
        pcmSampleCountRef.current += downsampled.length;

        if (pcmSampleCountRef.current >= samplesPerFlush) {
          const count = pcmSampleCountRef.current;
          const merged = new Float32Array(count);
          let offset = 0;
          for (const buf of pcmBufferRef.current) {
            merged.set(buf, offset);
            offset += buf.length;
          }
          pcmBufferRef.current = [];
          pcmSampleCountRef.current = 0;

          // Skip silence — compute RMS and discard if too quiet
          let sumSq = 0;
          for (let i = 0; i < merged.length; i++) {
            sumSq += merged[i] * merged[i];
          }
          const rms = Math.sqrt(sumSq / merged.length);
          if (rms < 0.005) {
            return;
          }

          const wavBase64 = pcmToWavBase64(merged, PCM_TARGET_RATE);
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            setRealtimeProcessing(true);
            wsRef.current.send(
              JSON.stringify({
                type: 'chunk',
                audio_base64: wavBase64,
                mime_type: 'audio/wav',
                is_cumulative: false,
              })
            );
          }
        }
      };

      source.connect(processor);
      processor.connect(audioCtx.destination);

      const ws = audioToolsService.connectRealtimeSpeechToText(token);
      wsRef.current = ws;

      ws.onopen = () => {
        setRealtimeStatus('recording');
      };

      ws.onmessage = (messageEvent) => {
        try {
          setRealtimeProcessing(false);
          const payload = JSON.parse(messageEvent.data);
          if (payload.type === 'partial') {
            const chunkText = payload.text || '';
            const combinedText = payload.combined_text || '';
            if (!chunkText && !combinedText) {
              realtimeEmptyPartialCountRef.current += 1;
              if (realtimeEmptyPartialCountRef.current >= 3) {
                setRealtimeError('No speech detected — check your microphone and speak clearly.');
              }
            } else {
              realtimeDecodeErrorCountRef.current = 0;
              realtimeEmptyPartialCountRef.current = 0;
              setRealtimeError(null);
            }
            setRealtimePartial(chunkText);
            setRealtimeTranscript((current) => combinedText || current);
            return;
          }
          if (payload.type === 'final') {
            realtimeDecodeErrorCountRef.current = 0;
            realtimeEmptyPartialCountRef.current = 0;
            setRealtimeError(null);
            setRealtimePartial('');
            setRealtimeTranscript(payload.text || '');
            if (
              realtimeFinalizeRequestedRef.current &&
              wsRef.current &&
              wsRef.current.readyState === WebSocket.OPEN
            ) {
              clearRealtimeCloseTimer();
              wsRef.current.close(1000, 'finalized');
            }
            return;
          }
          if (payload.type === 'error') {
            const detail = payload.detail || 'Realtime transcription failed.';
            if (detail.includes('Unable to decode audio')) {
              realtimeDecodeErrorCountRef.current += 1;
              if (realtimeDecodeErrorCountRef.current <= 2) {
                return;
              }
              setRealtimeError('Processing audio... keep speaking.');
              return;
            }
            setRealtimeError(detail);
            return;
          }
        } catch {
          setRealtimeError('Invalid realtime server response.');
        }
      };

      ws.onerror = () => {
        setRealtimeError('Realtime speech-to-text connection error.');
      };

      ws.onclose = () => {
        realtimeFinalizeRequestedRef.current = false;
        clearRealtimeCloseTimer();
        stopAudioCapture();
        releaseMediaStream();
        wsRef.current = null;
        pcmBufferRef.current = [];
        pcmSampleCountRef.current = 0;
        setRealtimeStatus('idle');
        setRealtimeProcessing(false);
      };
    } catch (error) {
      const message = 'Unable to access your microphone.';
      setRealtimeError(message);
      toast.error(message);
      resetRealtimeResources();
      setRealtimeStatus('idle');
      setRealtimeProcessing(false);
    }
  };

  const handleStopRealtime = () => {
    if (!wsRef.current && !scriptProcessorRef.current) {
      return;
    }

    setRealtimeStatus('stopping');
    realtimeFinalizeRequestedRef.current = true;
    stopAudioCapture();
    flushPCMBuffer();
    void finalizeRealtimeSession();
  };

  const handleVoiceChanger = async (event) => {
    event.preventDefault();
    if (!voiceChangerFile) {
      toast.error('Select a WAV or MP3 file for voice changer');
      return;
    }

    setIsVoiceChanging(true);
    try {
      const blob = await audioToolsService.voiceChanger(voiceChangerFile, {
        pitchShiftSemitones: voicePitch,
        speed: voiceSpeed,
        gainDb: voiceGain,
      });
      setBlobPreviewUrl(blob, voiceChangedUrlRef, setVoiceChangedUrl);
      toast.success('Voice changed successfully');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Voice changer failed';
      toast.error(message);
    } finally {
      setIsVoiceChanging(false);
    }
  };

  const handleGenerateSfx = async (event) => {
    event.preventDefault();
    if (!sfxPrompt.trim()) {
      toast.error('Enter a prompt for sound effects');
      return;
    }

    setIsGeneratingSfx(true);
    try {
      const result = await audioToolsService.textToSoundEffects(sfxPrompt.trim(), {
        durationSeconds: sfxDuration,
        intensity: sfxIntensity,
      });
      setSfxType(result.effectType || 'chime');
      setBlobPreviewUrl(result.audioBlob, sfxUrlRef, setSfxUrl);
      toast.success('Sound effect generated');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Sound effect generation failed';
      toast.error(message);
    } finally {
      setIsGeneratingSfx(false);
    }
  };

  const handleVoiceIsolation = async (event) => {
    event.preventDefault();
    if (!isolatorFile) {
      toast.error('Select a WAV or MP3 file for voice isolation');
      return;
    }

    setIsIsolating(true);
    try {
      const blob = await audioToolsService.voiceIsolator(isolatorFile, {
        strength: isolationStrength,
      });
      setBlobPreviewUrl(blob, isolatorUrlRef, setIsolatorUrl);
      toast.success('Voice isolation complete');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Voice isolation failed';
      toast.error(message);
    } finally {
      setIsIsolating(false);
    }
  };

  return (
    <div className="card mt-6">
      <h2 className="text-xl font-semibold mb-2">Advanced Audio Tools</h2>
      <p className="text-sm text-gray-400 mb-6">
        Speech to Text, Realtime Speech to Text, Voice Changer, Text to Sound Effects, and Voice
        Isolator.
      </p>

      <div className="space-y-6">
        {shouldShowSection('speech-to-text') && (
          <section className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Speech to Text</h3>
          <form className="space-y-3" onSubmit={handleSpeechToText}>
            <div>
              <label htmlFor="speech-to-text-file" className="block text-sm mb-1">
                Speech audio file
              </label>
              <input
                id="speech-to-text-file"
                type="file"
                accept="audio/*"
                className="input"
                onChange={(e) => setSpeechFile(e.target.files?.[0] || null)}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isTranscribing || !speechFile}>
              {isTranscribing ? 'Transcribing...' : 'Transcribe Audio'}
            </button>
          </form>

          {speechResult && (
            <div className="mt-4 bg-gray-900/60 rounded-lg p-3 border border-gray-700">
              <p className="text-xs text-gray-400 mb-2">
                Language: {speechResult.language} • Duration: {speechResult.duration_seconds.toFixed(2)}s
              </p>
              <p className="text-sm whitespace-pre-wrap">{speechResult.text || '(No speech detected)'}</p>
            </div>
          )}
          </section>
        )}

        {shouldShowSection('realtime-speech-to-text') && (
          <section className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Realtime Speech to Text</h3>
          {!canUseRealtime && (
            <p className="text-sm text-yellow-400 mb-3">
              This browser does not support MediaRecorder/getUserMedia for realtime transcription.
            </p>
          )}

          {/* Microphone selector and test */}
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="mic-select" className="block text-sm mb-1">Microphone</label>
                <select
                  id="mic-select"
                  className="input w-full"
                  value={selectedMicId}
                  onChange={(e) => { setSelectedMicId(e.target.value); stopMicTest(); }}
                  disabled={realtimeStatus === 'recording' || realtimeStatus === 'connecting'}
                >
                  {micDevices.length === 0 && (
                    <option value="">Click &quot;Detect Mics&quot; to load devices</option>
                  )}
                  {micDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={refreshMicDevices}
                disabled={realtimeStatus === 'recording' || realtimeStatus === 'connecting'}
              >
                Detect Mics
              </button>
              <button
                type="button"
                className={micTestStatus === 'testing' ? 'btn btn-secondary border-red-500' : 'btn btn-secondary'}
                onClick={micTestStatus === 'testing' ? stopMicTest : startMicTest}
                disabled={!selectedMicId || realtimeStatus === 'recording'}
              >
                {micTestStatus === 'testing' ? 'Stop Test' : 'Test Mic'}
              </button>
            </div>
            {micTestStatus === 'testing' && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-16">Level:</span>
                <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-100 ${
                      micTestLevel > 30 ? 'bg-green-500' : micTestLevel > 5 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${micTestLevel}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-20">
                  {micTestLevel > 30 ? '✓ Working' : micTestLevel > 5 ? 'Quiet' : 'No signal'}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStartRealtime}
              disabled={realtimeStatus === 'recording' || realtimeStatus === 'connecting'}
            >
              {realtimeStatus === 'connecting' ? 'Connecting...' : 'Start Realtime STT'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleStopRealtime}
              disabled={realtimeStatus !== 'recording' && realtimeStatus !== 'stopping'}
            >
              {realtimeStatus === 'stopping' ? 'Stopping...' : 'Stop'}
            </button>
          </div>

          <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700">
            <p className="text-xs text-gray-400 mb-2">
              Status: <span className="uppercase">{realtimeStatus}</span>
            </p>
            {realtimeError && <p className="text-sm text-red-400 mb-2">{realtimeError}</p>}
            {realtimeStatus === 'recording' && !realtimeTranscript && (
              <div className="flex items-center gap-2 text-sm text-yellow-300 mb-2">
                <span className="inline-block w-4 h-4 border-2 border-yellow-300 border-t-transparent rounded-full animate-spin" />
                Listening… speak now
              </div>
            )}
            {realtimeStatus === 'recording' && realtimeProcessing && (
              <div className="flex items-center gap-2 text-sm text-blue-300 mb-2">
                <span className="inline-block w-4 h-4 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
                Transcribing audio…
              </div>
            )}
            {realtimePartial && (
              <p className="text-sm text-purple-300 mb-2">Current chunk: {realtimePartial}</p>
            )}
            <p className="text-sm whitespace-pre-wrap">
              {realtimeTranscript || 'Transcript will appear here while recording.'}
            </p>
          </div>
          </section>
        )}

        {shouldShowSection('voice-changer') && (
          <section className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Voice Changer</h3>
          <form className="space-y-3" onSubmit={handleVoiceChanger}>
            <div>
              <label htmlFor="voice-changer-file" className="block text-sm mb-1">
                Voice changer audio file (WAV/MP3)
              </label>
              <input
                id="voice-changer-file"
                type="file"
                accept=".wav,.mp3,audio/wav,audio/x-wav,audio/mpeg,audio/mp3"
                className="input"
                onChange={(e) => setVoiceChangerFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <label className="text-sm">
                Pitch (semitones): {voicePitch.toFixed(1)}
                <input
                  type="range"
                  min={-12}
                  max={12}
                  step={0.5}
                  value={voicePitch}
                  className="w-full"
                  onChange={(e) => setVoicePitch(Number(e.target.value))}
                />
              </label>
              <label className="text-sm">
                Speed: {voiceSpeed.toFixed(2)}x
                <input
                  type="range"
                  min={0.5}
                  max={2}
                  step={0.05}
                  value={voiceSpeed}
                  className="w-full"
                  onChange={(e) => setVoiceSpeed(Number(e.target.value))}
                />
              </label>
              <label className="text-sm">
                Gain (dB): {voiceGain.toFixed(1)}
                <input
                  type="range"
                  min={-20}
                  max={20}
                  step={0.5}
                  value={voiceGain}
                  className="w-full"
                  onChange={(e) => setVoiceGain(Number(e.target.value))}
                />
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isVoiceChanging || !voiceChangerFile}
            >
              {isVoiceChanging ? 'Processing...' : 'Apply Voice Changer'}
            </button>
          </form>

          {voiceChangedUrl && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-300">Processed audio preview</p>
              <audio controls src={voiceChangedUrl} className="w-full" />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => downloadBlobUrl(voiceChangedUrl, 'voice_changed.wav')}
              >
                Download Voice Changed WAV
              </button>
            </div>
          )}
          </section>
        )}

        {shouldShowSection('text-to-sound-effects') && (
          <section className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Text to Sound Effects</h3>
          <form className="space-y-3" onSubmit={handleGenerateSfx}>
            <div>
              <label htmlFor="sound-effects-prompt" className="block text-sm mb-1">
                Sound effect prompt
              </label>
              <input
                id="sound-effects-prompt"
                type="text"
                value={sfxPrompt}
                className="input"
                placeholder="e.g. laser zap, heavy thunder, whoosh"
                onChange={(e) => setSfxPrompt(e.target.value)}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <label className="text-sm">
                Duration: {sfxDuration.toFixed(1)}s
                <input
                  type="range"
                  min={0.2}
                  max={10}
                  step={0.1}
                  value={sfxDuration}
                  className="w-full"
                  onChange={(e) => setSfxDuration(Number(e.target.value))}
                />
              </label>
              <label className="text-sm">
                Intensity: {sfxIntensity.toFixed(2)}
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={sfxIntensity}
                  className="w-full"
                  onChange={(e) => setSfxIntensity(Number(e.target.value))}
                />
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isGeneratingSfx || !sfxPrompt.trim()}
            >
              {isGeneratingSfx ? 'Generating...' : 'Generate Sound Effect'}
            </button>
          </form>

          {sfxUrl && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-300">Generated effect type: {sfxType || 'unknown'}</p>
              <audio controls src={sfxUrl} className="w-full" />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => downloadBlobUrl(sfxUrl, `sound_effect_${sfxType || 'custom'}.wav`)}
              >
                Download Sound Effect WAV
              </button>
            </div>
          )}
          </section>
        )}

        {shouldShowSection('voice-isolator') && (
          <section className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Voice Isolator</h3>
          <form className="space-y-3" onSubmit={handleVoiceIsolation}>
            <div>
              <label htmlFor="voice-isolator-file" className="block text-sm mb-1">
                Voice isolator WAV/MP3 file
              </label>
              <input
                id="voice-isolator-file"
                type="file"
                accept=".wav,.mp3,audio/wav,audio/x-wav,audio/mpeg,audio/mp3"
                className="input"
                onChange={(e) => setIsolatorFile(e.target.files?.[0] || null)}
              />
            </div>

            <label className="text-sm">
              Isolation strength: {isolationStrength.toFixed(2)}
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={isolationStrength}
                className="w-full"
                onChange={(e) => setIsolationStrength(Number(e.target.value))}
              />
            </label>

            <button type="submit" className="btn btn-primary" disabled={isIsolating || !isolatorFile}>
              {isIsolating ? 'Isolating...' : 'Run Voice Isolator'}
            </button>
          </form>

          {isolatorUrl && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-300">Isolated voice preview</p>
              <audio controls src={isolatorUrl} className="w-full" />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => downloadBlobUrl(isolatorUrl, 'voice_isolated.wav')}
              >
                Download Isolated WAV
              </button>
            </div>
          )}
          </section>
        )}
      </div>
    </div>
  );
}

export default AudioToolsPanel;
